// Analytics utility functions for tracking events
declare global {
  interface Window {
    pirsch: (eventName: string, options?: { duration?: number; meta?: Record<string, any> }) => Promise<void>;
    gizmo: ((eventName: string, properties?: Record<string, any>) => void) & { session?: () => string | null };
  }
}

// Gizmo custom events (clean names, used for funnels/goals in the Gizmo dashboard)
export const trackGizmo = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window === "undefined") return;
  // Capture the source immediately. A delayed tracker can load after a client
  // navigation; attributing the event to that later page corrupts the funnel.
  const eventProperties = { ...properties, from_page: window.location.pathname };
  const send = (attempt: number) => {
    try {
      if (typeof window.gizmo === "function") {
        window.gizmo(eventName, eventProperties);
      } else if (attempt < 20) {
        setTimeout(() => send(attempt + 1), 500);
      }
    } catch (error) {
      console.error("Error tracking gizmo event:", error);
    }
  };
  send(0);
};

// Wait for Pirsch to be ready
const waitForPirsch = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  // If Pirsch is already loaded, return immediately
  if ("pirsch" in window) return;

  // Wait for the script to load
  return new Promise((resolve) => {
    const checkPirsch = () => {
      if ("pirsch" in window) {
        resolve();
      } else {
        setTimeout(checkPirsch, 100);
      }
    };
    checkPirsch();
  });
};

export const trackEvent = async (eventName: string, options?: { duration?: number; meta?: Record<string, any> }) => {
  if (typeof window === "undefined") return;

  try {
    await waitForPirsch();
    await window.pirsch(eventName, options);
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};

// AI Color Analysis specific events
export const trackAnalysisStart = (tool: string) => {
  trackEvent(`1 - [${tool}] Viewed Prices`);
};

export const choseFreeTrial = (tool: string) => {
  trackEvent(`1.5 - [${tool}] Chose Free Trial`);
};

export const trackPackageSelected = (tool: string, selectedPackage: string) => {
  trackEvent(`2 - [${tool}] Package Selected`, {
    meta: {
      selectedPackage,
    },
  });
};

export const trackPaymentInitiated = (tool: string, selectedPackage: string) => {
  trackEvent(`3 - [${tool}] Checkout Initiated`, {
    meta: {
      selectedPackage,
    },
  });
};

export const trackPaymentReceived = (tool: string, selectedPackage: string) => {
  trackEvent(`4 - [${tool}] Payment Received`, {
    meta: {
      selectedPackage,
    },
  });
};

export const trackAnalysisCompleted = (tool: string, analysisId: string) => {
  trackEvent(`5 - [${tool}] Completed`, {
    meta: {
      analysisId,
    },
  });
};

// Quiz/Tool funnel events
export const trackQuizStart = (quizName: string) => {
  trackGizmo("quiz_start", { quiz: quizName });
  trackEvent(`[${quizName}] 1 - Started`);
};

export const trackQuizComplete = (quizName: string) => {
  trackEvent(`[${quizName}] 2 - Completed`);
  trackGizmo("quiz_complete", { quiz: quizName });
};

// Funnel events (Gizmo). Purchase is deduped per Stripe session so page
// reloads on the success page don't double-count.
const checkoutStartFired = new Set<string>();
const fulfillmentFired = new Set<string>();
const purchaseFired = new Set<string>();

// The identifier is used only for local deduplication, never sent to analytics.
// This records a report delivered to the browser, not Stripe revenue.
export const trackFulfillment = (tool: string, resultId: string, properties?: Record<string, string>) => {
  if (typeof window === "undefined" || !resultId) return;
  const key = `tracked_fulfillment_${tool}_${resultId}`;
  if (fulfillmentFired.has(key)) return;
  try { if (window.localStorage.getItem(key)) return; } catch { /* Storage can be unavailable. */ }
  fulfillmentFired.add(key);
  try { window.localStorage.setItem(key, "1"); } catch { /* The in-memory guard still prevents repeats. */ }
  trackGizmo("report_fulfilled", { tool, ...properties });
};
export const trackCheckoutStart = (tool: string, experimentMeta?: Record<string, any>) => {
  // Some wizards re-create the Stripe session (e.g. upsell toggles); only
  // count the first checkout start per tool per pageload.
  if (checkoutStartFired.has(tool)) return;
  checkoutStartFired.add(tool);
  trackEvent(`3 - [${tool}] Checkout Initiated`, experimentMeta ? { meta: experimentMeta } : undefined);
  // experimentMeta carries { experiment, bucket, price_cents } for price A/B
  // tests so Gizmo can compute revenue-per-visitor per arm.
  trackGizmo("checkout_start", { tool, ...experimentMeta });
};

export const trackPurchase = (tool: string, sessionId?: string, experimentMeta?: Record<string, any>) => {
  if (typeof window !== "undefined" && sessionId) {
    const key = `tracked_purchase_${sessionId}`;
    if (purchaseFired.has(key)) return;
    try { if (localStorage.getItem(key)) return; } catch { /* Storage may be blocked. */ }
    purchaseFired.add(key);
    try { localStorage.setItem(key, "1"); } catch { /* The in-memory guard still deduplicates. */ }
  }
  trackEvent(`4 - [${tool}] Payment Received`, experimentMeta ? { meta: experimentMeta } : undefined);
  trackGizmo("purchase", { tool, ...experimentMeta });
};

export const trackEmailCapture = (source: string) => {
  trackEvent(`[Email] Captured`, { meta: { source } });
  trackGizmo("email_capture", { source });
};

export const trackPhotoUpload = (quizName: string) => {
  trackEvent(`[${quizName}] 3 - Photo Uploaded`);
  trackGizmo("photo_upload", { quiz: quizName });
};

export const trackToolPhotoUpload = (toolName: string) => {
  trackEvent(`[${toolName}] 3 - Photo Uploaded`);
  trackGizmo("tool_photo_upload", { tool: toolName });
};

export const trackCtaClick = (source: string) => {
  trackEvent(`[CTA] Clicked`, { meta: { source } });
  trackGizmo("cta_click", { source });
};

// Affiliate product click tracking
export const trackProductClick = (productName: string, brand: string, category: "lipstick" | "hair", season: string) => {
  trackEvent(`[Affiliate] Product Click`, {
    meta: {
      product: productName,
      brand,
      category,
      season,
    },
  });
};
